/**
 * TaskBadgeSubscriber - pushes sidebar task badge counts via SignalR.
 *
 * Pattern (Phase 3 pilot of the badge migration):
 *   1. Pre-check the user's roles. If the user can't satisfy the hub's
 *      [CcmsHubAuthorize] gate, skip the WS connection entirely so we don't
 *      spam the server with forbidden negotiate attempts.
 *   2. Fetch the authoritative count baseline ONCE via REST on mount and on
 *      each (re)connect. This is the only REST call this component makes.
 *   3. Subscribe to the taskmanagement dashboard hub. Apply incoming
 *      BadgeDelta events directly to the zustand store; BadgeSnapshot
 *      replaces the baseline when the server has authoritative state.
 *
 * Failure modes:
 *   - WS handshake fails → store is still populated from the REST baseline.
 *   - WS drops mid-session → SDK auto-reconnects with exponential backoff;
 *     on reconnect we re-fetch the baseline to recover any deltas missed
 *     during the gap.
 *   - User lacks role → component renders nothing, no connection attempted.
 */

import { useCallback, useEffect, useRef } from 'react';
import {
  useTaskPortalTaskService,
  useTaskPortalDashboardService,
  useDomainDashboard,
  type BadgeDeltaMessage,
  type BadgeSnapshotMessage,
} from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import {
  useTaskBadgesStore,
  type TaskBadgeMetrics,
  type TaskBadgeDeltas,
} from './taskBadgesStore';

const DEBUG = import.meta.env.DEV;
const log = (...args: any[]) => {
  if (DEBUG) console.log('[TaskBadgeSubscriber]', ...args); // eslint-disable-line no-console
};

const TASK_ROLES = ['Tasks Access', 'Admin'];

// Mirrors TASK_STATUS in pages/app/TaskManagement/utils/taskConstants.ts -
// 0 Unseen · 1 Seen · 2 On it · 3 Done · 4 Closed · 5+ etc.
// "Active" tasks for the badge are statuses 0/1/2 (work still owed).
const ACTIVE_STATUSES = new Set([0, 1, 2]);

export function TaskBadgeSubscriber() {
  const { hasAnyRole } = useEnrichedAuth();
  const enabled = hasAnyRole(TASK_ROLES);

  const taskService = useTaskPortalTaskService();
  const dashboardService = useTaskPortalDashboardService();
  const setFromMetrics = useTaskBadgesStore((s) => s.setFromMetrics);
  const applyDelta = useTaskBadgesStore((s) => s.applyDelta);
  const reset = useTaskBadgesStore((s) => s.reset);

  // Baseline: a single REST refetch per (re)connect, NOT a periodic poll.
  // Kept as a callback so reconnect can call it from inside the WS hook.
  // `unseenAssigned`/`overdueAssigned` are also kept live by WS deltas;
  // `loopedInActive`/`myTasksActive` have no events, so this baseline is
  // their only source of truth.
  const refetchBaseline = useCallback(async () => {
    // Upcoming is fetched separately, and deliberately not inside the Promise.all
    // below: scheduled tasks sit behind the EnableScheduledTasks flag, so this
    // call can legitimately fail or return nothing on an instance where the
    // feature is dark. Sharing a rejection with the others would take down all
    // four counts over a feature that is simply switched off.
    void (async () => {
      try {
        const view: any = await dashboardService.getUpcoming();
        const upcoming = (view?.scheduledTasks ?? []).length;
        log('baseline upcoming', upcoming);
        setFromMetrics({ upcoming });
      } catch (err) {
        log('upcoming baseline fetch failed', err);
      }
    })();

    try {
      const [myTasks, loopedIn, created] = await Promise.all([
        taskService.getMyTasks({ take: 10000 } as any) as Promise<any[]>,
        taskService.getLoopedInTasks() as Promise<any[]>,
        taskService.getCreatedTasks({ take: 10000 } as any) as Promise<any[]>,
      ]);
      const isActive = (t: any) => ACTIVE_STATUSES.has(Number(t?.statusID));
      const unseenAssigned = myTasks.filter(isActive).length;
      const overdueAssigned = myTasks.filter((t: any) => t?.isOverdue).length;
      const loopedInActive = loopedIn.filter(isActive).length;
      const myTasksActive = created.filter(isActive).length;
      log('baseline', { unseenAssigned, overdueAssigned, loopedInActive, myTasksActive });
      setFromMetrics({ unseenAssigned, overdueAssigned, loopedInActive, myTasksActive });
    } catch (err) {
      log('baseline fetch failed', err);
      // Leave previous store state in place - the WS deltas can still keep
      // counts moving in the right direction. The next reconnect retries.
    }
  }, [taskService, dashboardService, setFromMetrics]);

  // Initial baseline once the user is known to be a role match.
  useEffect(() => {
    if (!enabled) return;
    refetchBaseline();
  }, [enabled, refetchBaseline]);

  // SignalR - connection + badge events.
  const { isConnected, requestBadgeSnapshot } = useDomainDashboard('taskmanagement', {
    autoConnect: enabled,
    autoSubscribe: false, // we don't need ReceiveDashboardMetrics for badges
    onBadgeSnapshot: (msg: BadgeSnapshotMessage<TaskBadgeMetrics>) => {
      if (msg.domain !== 'TaskManagement') return;
      log('snapshot', msg.snapshot);
      setFromMetrics(msg.snapshot ?? {});
    },
    onBadgeDelta: (msg: BadgeDeltaMessage<TaskBadgeDeltas>) => {
      if (msg.domain !== 'taskmanagement') return;
      log('delta', msg.reason, msg.deltas);
      applyDelta(msg.deltas ?? {});
    },
  });

  // Re-baseline on reconnect: when we go from disconnected → connected after
  // having been connected before, the WS may have dropped any deltas during
  // the gap. Re-fetch authoritative counts and ask the server for a fresh
  // snapshot in case it populated one.
  const wasConnectedRef = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    if (isConnected && !wasConnectedRef.current) {
      // first connect - baseline already fetched by the mount effect above
      wasConnectedRef.current = true;
      return;
    }
    if (isConnected && wasConnectedRef.current === false) return;
    if (!isConnected && wasConnectedRef.current) {
      // dropped - mark for resync on next connect
      log('connection dropped');
      return;
    }
    if (isConnected && wasConnectedRef.current) {
      log('reconnected → refetch baseline');
      refetchBaseline();
      void requestBadgeSnapshot();
    }
  }, [enabled, isConnected, refetchBaseline, requestBadgeSnapshot]);

  useEffect(() => () => reset(), [reset]);

  useEffect(() => {
    log('mounted', { enabled });
  }, [enabled]);

  return null;
}
